import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { ISchemaComponentProps, SchemaFormComponentWrapper, ValueDispatch, ValueAction } from '@restspace/schema-form';
import { EditorState } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { stateFromHTML, Options } from 'draft-js-import-html';

// filter to make target attribute work on export to HTML
let exportOptions: any = {
    entityStyleFn: (entity:any) => {
      const entityType = entity.get('type').toLowerCase();
      if (entityType === 'link') {
        const data = entity.getData();
        return {
          element: 'a',
          attributes: {
            target: data.targetOption,
            href: data.url
          }
        };
      }
    }
  };

// filter to make target attribute work on import from HTML
let importOptions: Options = {
    customInlineFn: (element: any, { Entity }: any) => {
        if (element.tagName === 'A' && element.target) {
            return Entity("LINK", {
                targetOption: element.target,
                url: element.href
            });
        }
    }
};

export function HtmlEditor(props: ISchemaComponentProps) {
    const {
        path,
        value,
        onFocus,
        onBlur
    } = props;
    const dispatch: React.Dispatch<ValueAction> = useContext(ValueDispatch);
    const [currentValue, setCurrentValue] = useState(value);
    const [editorState, setEditorState] = useState<EditorState>(valueToEditorState(value));
    const [editorKey, setEditorKey] = useState(0);
    const timeoutId = useRef<number>(0);
    const doUpdate = useRef<any>(null);

    useEffect(() => {
        if (value !== currentValue) { // don't update from props.value when props.value was changed as a result of calling props.onChange
            setEditorState(valueToEditorState(value));
            setEditorKey(key => key + 1); // when props.value is changed, we need to unmount and recreate the editor for its contents to change
            setCurrentValue(value);
        }
    }, [value]);
    
    const onEditorStateChange = useCallback((newEditorState: EditorState) => {
        if (timeoutId.current) window.clearTimeout(timeoutId.current);
        doUpdate.current = () => {
            setEditorState(newEditorState);
            const newValue = stateToHTML(newEditorState.getCurrentContent(), exportOptions);
            setCurrentValue(newValue);
            dispatch(ValueAction.set(path, newValue));
        };
        timeoutId.current = window.setTimeout(doUpdate.current, 750);
    }, [ dispatch ]);

    function valueToEditorState(val: string) {
        const contentState = stateFromHTML(val || '', importOptions);
        return EditorState.createWithContent(contentState);
    }

    const onEditorBlur = useCallback(() => {
        if (timeoutId.current) window.clearTimeout(timeoutId.current);
        if (doUpdate.current) doUpdate.current();
        onBlur(path);
    }, [ onBlur ]);

    const onEditorFocus = useCallback(() => onFocus(path), [ onFocus ]);

    return (
        <SchemaFormComponentWrapper {...props}>
            <div className="sf-control sf-html-editor">
                <Editor key={editorKey}
                    defaultEditorState={editorState}
                    onBlur={onEditorBlur}
                    onFocus={onEditorFocus}
                    onEditorStateChange={onEditorStateChange}/>
            </div>
        </SchemaFormComponentWrapper>
    );
}